import {Injectable} from '@angular/core';
import {Dataset} from '../../model/dataset/dataset';
import {Account} from '../../model/account/account';
import {ImageOverlay} from '../../model/overlay/external/image-overlay';
import {MapInteractionMode} from './map-interaction-mode';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {DatasetField} from '../../model/dataset/field/dataset-field';
import {DatasetGeometryType} from '../../model/dataset/dataset-geometry-type';
import {ObjectUtils} from '../../core/utils/object-utils';
import {DatasetFieldScope} from '../../model/dataset/dataset-field-scope';
import {MapInteractionStatus} from './map-interaction-status';
import {defaultMapStatus} from '../../core/map/map.constants';

/**
 * This is a service that stores everything related to the active context of a datapoints page (table or map).
 * On leave page we should clean this up.
 */
@Injectable()
export class DatapointsPageStateService {

    datasetsByIds: Map<string, Dataset> = new Map(); // all datasets related to an account (including account applications). This is for fast accessing.
    activeDatasetsOnMap: Dataset[] = []; // only the map component should touch this
    activeImageOverlaysOnMap: ImageOverlay[] = []; // only the map component should touch this
    activeGroups: number[];
    activeAccountId: number; // we can have this sooner than the actual account
    activeAccount: Account;
    private activeDataset: Dataset;
    private savedSelectedOverlay: Dataset | null = null;
    private savedActiveDatasetsOnMap: Dataset[] = [];
    private savedActiveImageOverlaysOnMap: ImageOverlay[] = [];
    private savedFilterTreeState: any = null;
    private savedDatasetIsDisabled: boolean = false;
    private activeDatasetFields: Map<string, DatasetField> = new Map();
    private activateDatasetTessadataFields: { nriFields: DatasetField[], externalFields: DatasetField[],/* tensorflightFields: DatasetField[] ,*/ e2valueFields: DatasetField[], munichreFields: DatasetField[], customScoreFields:DatasetField[], preciselyBuildingFields:DatasetField[], consolidatedRiskScoreFields:DatasetField[], preciselyCrimeFields:DatasetField[], preciselyWildfireRiskFields:DatasetField[] } = {
        nriFields: [],
        externalFields: [],
       // tensorflightFields: [],
        e2valueFields:[],
        munichreFields: [],
        customScoreFields: [],
        preciselyBuildingFields: [],
        consolidatedRiskScoreFields: [],
        preciselyCrimeFields: [],
        preciselyWildfireRiskFields: []
    };
    accountOverlays: Dataset[] = []; // private + global
    accountDatasets: Dataset[] = [];
    private linkedAccountOverlays: Dataset[] = []; // overlays that are linked and assigned to the active dataset
    private linkedAccountDatasets: Dataset[] = []; // account application datasets that are linked to the active dataset
    private filterAccountDatasets: Dataset[]; // account application datasets that are available for filtering
    private mapStatus: MapInteractionStatus = {mode:MapInteractionMode.DEFAULT}; // if the default, thematic map, or flood map are ON.

    private updateMapModeSubject: Subject<MapInteractionStatus> = new BehaviorSubject({mode:MapInteractionMode.DEFAULT});
    private datapointsChangedSubject: Subject<void> = new Subject<void>();

    getDataset(id: string): Dataset {
        return this.datasetsByIds.get(id);
    }

    setActiveDataset(dataset: Dataset) {
        this.activeDataset = dataset;
        if (dataset.geometryType === DatasetGeometryType.POINT) { // we have locations
            dataset.fields.forEach(field => {
                this.activeDatasetFields.set(field.id, field);
                if (field.isGenerated) {
                    if (field.scope === DatasetFieldScope.EXTERNAL) {
                        this.activateDatasetTessadataFields.externalFields.push(field);
                    } else if (field.scope === DatasetFieldScope.NRI) {
                        this.activateDatasetTessadataFields.nriFields.push(field);
                   // } else if (field.scope === DatasetFieldScope.TENSORFLIGHT) {
                        field.selected = false;
                     //   this.activateDatasetTessadataFields.tensorflightFields.push(field);
                    }else if (field.scope === DatasetFieldScope.E2VALUE) {
                        field.selected = false;
                        this.activateDatasetTessadataFields.e2valueFields.push(field);
                    } else if (field.scope === DatasetFieldScope.MUNICHRE) {
                        field.selected = false;
                        this.activateDatasetTessadataFields.munichreFields.push(field);
                    } else if(field.scope === DatasetFieldScope.CUSTOM_SCORE){
                        field.selected = false;
                        this.activateDatasetTessadataFields.customScoreFields.push(field);
                    }else if(field.scope === DatasetFieldScope.PRECISELY_BUILDING){
                        field.selected = false;
                        this.activateDatasetTessadataFields.preciselyBuildingFields.push(field);
                    }else if(field.scope === DatasetFieldScope.CONSOLIDATED_RISK_SCORE){
                        field.selected = false;
                        this.activateDatasetTessadataFields.consolidatedRiskScoreFields.push(field);
                    }else if(field.scope === DatasetFieldScope.PRECISELY_CRIME){
                        field.selected = false;
                        this.activateDatasetTessadataFields.preciselyCrimeFields.push(field);
                    }else if(field.scope === DatasetFieldScope.PRECISELY_WILDFIRE_RISK){
                        field.selected = false;
                        this.activateDatasetTessadataFields.preciselyWildfireRiskFields.push(field);
                    }
                }
            });
        }
    }

    getActiveDataset() {
        return this.activeDataset;
    }

    /**
     * This method will return a clone.
     */
    getLinkedAccountDatasets() {
        return ObjectUtils.clone(this.linkedAccountDatasets);
    }

    getFilterAccountDatasetsCloned(): Dataset[] {
        return ObjectUtils.clone(this.filterAccountDatasets);
    }

    getFilterAccountDatasets(): Dataset[] {
        return this.filterAccountDatasets;
    }

    setFilterAccountDatasets(value: Dataset[]) {
        this.filterAccountDatasets = value;
    }

    /**
     * This method will return a clone.
     */
    getLinkedAccountOverlays() {
        return ObjectUtils.clone(this.linkedAccountOverlays);
    }

    getLinkedAccountDatasetsNotCloned() {
        return this.linkedAccountDatasets;
    }

    getLinkedAccountOverlaysNotCloned() {
        return this.linkedAccountOverlays;
    }


    setLinkedAccountOverlays(value: Dataset[]): void {
        this.linkedAccountOverlays = value;
    }

    setLinkedAccountDatasets(value: Dataset[]) {
        this.linkedAccountDatasets = value;
    }


    getActiveTessadataFields() {
        return this.activateDatasetTessadataFields;
    }

    storeDataset(dataset: Dataset) {
        this.datasetsByIds.set(dataset.id, dataset);
    }

    reset() {
        this.datasetsByIds = new Map();
        this.activeDatasetFields = new Map();
        this.activeDatasetsOnMap = [];
        this.mapStatus = defaultMapStatus;
        this.updateMapModeSubject = new BehaviorSubject(defaultMapStatus);
        this.datapointsChangedSubject = new Subject<void>();
        this.activeDataset = null;
        this.activateDatasetTessadataFields = {nriFields: [], externalFields: [],/* tensorflightFields: [],*/ e2valueFields: [], munichreFields: [], customScoreFields:[], preciselyBuildingFields:[], consolidatedRiskScoreFields:[], preciselyCrimeFields:[], preciselyWildfireRiskFields:[] };
    }

    updateMapMode(newState: MapInteractionStatus) {
        this.mapStatus = newState;
        this.updateMapModeSubject.next(newState);
    }

    onMapInteractionChange(): Observable<MapInteractionStatus> {
        return this.updateMapModeSubject;
    }

    emitDatapointsChanged(): void {
        this.datapointsChangedSubject.next();
    }

    onDatapointsChanged(): Observable<void> {
        return this.datapointsChangedSubject.asObservable();
    }

    getMapInteractionStatus() {
        return this.mapStatus;
    }


    getActiveDatasetFields(): Map<string, DatasetField> {
        return this.activeDatasetFields;
    }

    saveSelectedOverlay(selectedOverlay: Dataset | null) {
        this.savedSelectedOverlay = selectedOverlay ? ObjectUtils.clone(selectedOverlay) : null;
    }

    getSavedSelectedOverlay(): Dataset | null {
        return this.savedSelectedOverlay;
    }

    saveOverlayState() {
        // Only save datasets that are actually selected (checked) in the Layers panel
        // This ensures unchecked Locations don't get restored when switching back to Map view
        this.savedActiveDatasetsOnMap = ObjectUtils.clone(
            this.activeDatasetsOnMap.filter(dataset => {
                const datasetInPanel = this.datasetsByIds.get(dataset.id);
                return datasetInPanel && datasetInPanel.isSelected !== false;
            })
        );
        this.savedActiveImageOverlaysOnMap = ObjectUtils.clone(this.activeImageOverlaysOnMap);
    }

    getSavedActiveDatasetsOnMap(): Dataset[] {
        return this.savedActiveDatasetsOnMap;
    }

    getSavedActiveImageOverlaysOnMap(): ImageOverlay[] {
        return this.savedActiveImageOverlaysOnMap;
    }

    saveDatasetIsDisabled(isDisabled: boolean) {
        this.savedDatasetIsDisabled = isDisabled;
    }

    getSavedDatasetIsDisabled(): boolean {
        return this.savedDatasetIsDisabled;
    }

    saveFilterTreeState(filterTreeState: any) {
        this.savedFilterTreeState = ObjectUtils.clone(filterTreeState);
    }

    getSavedFilterTreeState(): any {
        return this.savedFilterTreeState;
    }

    clearSavedFilterTreeState() {
        this.savedFilterTreeState = null;
    }
}
